import {
  type AddOnCalc,
  type AddOnCalcMethod,
  type AddOnItem,
  type PatternKind,
  type RoomProduct,
  type Subfloor,
  type SubfloorCondition,
} from "./document"

// Trade wording for the material schedule. The schedule is read by a
// customer's installer, so nothing on it may print a database value — the
// existing document leaked `front_to_back` into a Direction column, which is
// exactly what this module exists to prevent.
//
// Pure and dependency-free (vitest-covered). Every surface that shows a spec
// value — the printed document AND the editor inspector — calls through here,
// so the page and the panel can never disagree.

export const EM_DASH = "—"

/**
 * The trade's own words for our five pattern kinds, taken from RT's document:
 * it writes "Brick 1/3" where the schema says `third`, and "Random" where the
 * schema says `stagger`. Presentation only — PatternKind is unchanged.
 */
export const PATTERN_LABELS: Record<PatternKind, string> = {
  straight: "Straight",
  brick: "Brick 1/2",
  third: "Brick 1/3",
  herringbone: "Herringbone",
  stagger: "Random",
}

export function patternLabel(pattern: PatternKind | null | undefined): string {
  return pattern ? PATTERN_LABELS[pattern] : EM_DASH
}

/**
 * Layout angle as a direction an installer can act on.
 *
 * Plans are drawn with the front of the house at the bottom, so a pattern
 * running up and down the page runs front to back (RT, 2026-08-07). Rows
 * repeat every 180°, so 270° reads the same as 90°; anything off the four
 * canonical axes prints its degrees rather than a word that would be a lie.
 *
 * Roll goods carry no pattern and therefore no meaningful direction — they
 * print an em dash, matching the carpet rows on the existing document.
 */
export function directionLabel(
  angle: number | null | undefined,
  pattern: PatternKind | null | undefined
): string {
  if (pattern == null || angle == null || !Number.isFinite(angle)) return EM_DASH
  const a = ((angle % 180) + 180) % 180
  switch (a) {
    case 0:
      return "Side to side"
    case 90:
      return "Front to back"
    case 45:
      return "Diagonal 45°"
    case 135:
      return "Diagonal 135°"
    default:
      return `${Math.round(angle)}°`
  }
}

export function subfloorLabel(subfloor: Subfloor | null | undefined): string {
  switch (subfloor) {
    case "concrete":
      return "Concrete"
    case "wood":
      return "Wood"
    default:
      // Both an explicit "unspecified" and a missing value print the gap.
      return EM_DASH
  }
}

/** The material column: the catalog's product type ("LVP", "Carpet", "Tile"). */
export function materialLabel(product: RoomProduct | null | undefined): string {
  return product?.product_type || EM_DASH
}

export function styleLabel(product: RoomProduct | null | undefined): string {
  if (!product) return EM_DASH
  // Style plus the format the trade names it by — "Kudos 12x24", not "Kudos".
  return [product.style, product.format_label].filter(Boolean).join(" ") || EM_DASH
}

export function colorLabel(product: RoomProduct | null | undefined): string {
  return product?.color || EM_DASH
}

export function groutLabel(grout: string | null | undefined): string {
  return grout?.trim() || EM_DASH
}

/**
 * Instruction lists join with " ; " — the separator the existing document
 * already uses, which is why notes are stored as a list in the first place.
 */
export function notesLine(notes: string[] | null | undefined): string {
  const parts = (notes ?? []).map((n) => n.trim()).filter(Boolean)
  return parts.length > 0 ? parts.join(" ; ") : EM_DASH
}

/**
 * Order-quantity units, normalized to the trade's short forms. The replica's
 * live vocabulary is exactly SF · SY · LF · EA · RL (surveyed 2026-08-10);
 * manual add-ons may add GAL, BAG, TUBE, PAIL, CTN. Unknown or missing
 * prints the gap — never a guessed unit on a purchasing document.
 */
export function unitLabel(unit: string | null | undefined): string {
  const u = unit?.trim().toUpperCase()
  return u || EM_DASH
}

/** What an add-on line calls itself: style · colour for catalog items, the typed name for manual ones. */
export function addOnItemLabel(item: AddOnItem | null | undefined): string {
  if (!item) return EM_DASH
  if (item.source === "manual") return item.name.trim() || EM_DASH
  return [item.style, item.color].filter(Boolean).join(" · ") || item.id
}

/** The add-on's material column: catalog product type, or "Add-on" for manual items. */
export function addOnMaterialLabel(item: AddOnItem | null | undefined): string {
  if (!item) return EM_DASH
  return (item.source === "fc_replica" && item.product_type) || "Add-on"
}

/** "Concrete subfloors only" / "Wood subfloors only" — the condition chip's words. */
export function conditionLabel(condition: SubfloorCondition | null | undefined): string | null {
  if (!condition) return null
  return condition === "concrete" ? "Concrete subfloors only" : "Wood subfloors only"
}

export const METHOD_LABELS: Record<AddOnCalcMethod, string> = {
  room_area: "Room area",
  room_perimeter: "Room perimeter",
  hole_area: "Deduction area",
  hole_perimeter: "Deduction perimeter",
  count: "Count",
  manual: "Manual quantity",
}

/**
 * One line saying how an add-on's number is made — "Room area · 1 GAL per
 * 200 SF · min 2", "Count · 2 per room", "Manual · 6 EA". The panel rows and
 * both printed documents share this wording.
 */
export function calcSummary(calc: AddOnCalc, unit: string | null | undefined): string {
  const parts: string[] = [METHOD_LABELS[calc.calc_method]]
  const u = unitLabel(unit)
  if (calc.calc_method === "count") {
    parts.push(`${calc.count_qty ?? 0} per room`)
  } else if (calc.calc_method === "manual") {
    parts.push(`${calc.manual_qty ?? 0} ${u}`)
  } else if (calc.coverage_rate != null) {
    const basis = calc.calc_method.endsWith("perimeter") ? "LF" : "SF"
    parts.push(`1 ${u} per ${calc.coverage_rate} ${basis}`)
  }
  if (calc.min_qty != null) parts.push(`min ${calc.min_qty}`)
  return parts.join(" · ")
}
